import { useState } from 'react';
import {
  AritoIcon,
  BulkDeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { ActionBar, DeleteDialog, PaymentTermDialog } from './components';
import { toast, useCRUD, useExcelProcessor, useForms } from '@/hooks';
import { HanThanhToan, HanThanhToanInput } from '@/types/schemas';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { getDataTableColumns } from './cols-definition';
import type { PaymentTermSubmitData } from './schema';
import { ExcelConfig, ImportBody } from './utils';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function PaymentTermPage() {
  // Excel import state
  const [isLoadingImport, setIsLoadingImport] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const { processFile } = useExcelProcessor(ExcelConfig);

  const {
    data: paymentTerms,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addPaymentTerm,
    updateItem: updatePaymentTerm,
    deleteItem: deletePaymentTerm,
    refreshData: refreshPaymentTerms,
    searchData
  } = useCRUD<HanThanhToan, HanThanhToanInput>({
    endpoint: QUERY_KEYS.HAN_THANH_TOAN,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    showBulkDelete,
    isImportDialogOpen,
    setIsImportDialogOpen,

    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleOpenImportExcel
  } = useForms<HanThanhToan>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : paymentTerms || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddPaymentTerm = async (data: PaymentTermSubmitData) => {
    await addPaymentTerm(data as HanThanhToanInput);
    await refreshPaymentTerms();
  };

  const handleUpdatePaymentTerm = async (data: PaymentTermSubmitData) => {
    if (data.uuid && selectedObj) {
      await updatePaymentTerm(selectedObj.uuid, data as HanThanhToanInput);
      await refreshPaymentTerms();
    }
  };

  // Excel import handlers
  const handleImportBodies = async (bodies: ImportBody[]) => {
    let successCount = 0;
    setIsLoadingImport(true);
    try {
      for (const body of bodies) {
        await addPaymentTerm(body);
        successCount++;
      }
      toast({
        description: 'Đã nhập danh mục hạn thanh toán từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      await refreshPaymentTerms();
      setIsLoadingImport(false);
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={() => selectedObj && handleEditClick()}
        onDeleteIconClick={() => selectedObj && handleDeleteClick()}
        onCopyIconClick={() => selectedObj && handleCopyClick()}
        onWatchIconClick={() => selectedObj && handleViewClick()}
        onImportFromExcel={handleOpenImportExcel}
      />

      <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <PaymentTermDialog
          open={showForm}
          mode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          selectedObj={formMode === 'edit' || formMode === 'view' ? selectedObj : null}
          addPaymentTerm={handleAddPaymentTerm}
          updatePaymentTerm={handleUpdatePaymentTerm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={handleCopyClick}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deletePaymentTerm={deletePaymentTerm}
          clearSelection={clearSelection}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Hạn thanh toán'
          onClose={handleCloseBulkDelete}
          columns={getDataTableColumns()}
          dataRows={displayData}
          handleDelete={deletePaymentTerm}
        />
      )}
      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}
